import React, { useState, useMemo, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { useField } from 'react-final-form';
import { FlexColumn, FlexMiddle, FlexSpace, LoaderArea } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useEmployeeBookedShifts from '@ubeya/shared/hooks/account/useEmployeeBookedShifts';
import useDebounce from '@ubeya/shared/hooks/useDebounce';
import Search from '@ubeya/shared-web/components/Search/Search';
import { ReactComponent as SearchIcon } from '@ubeya/shared-web/assets/help-desk/search.svg';
import { formatShiftTimeRange } from '../../helpDeskUtils';

const TABS = ['upcoming', 'past'];

const toShiftDetails = (shift) => ({
  shiftId: shift.shiftId,
  projectName: shift.projectName,
  positionName: shift.positionName,
  date: shift.date,
  startTime: shift.startTime,
  endTime: shift.endTime
});

const Container = styled(FlexColumn)`
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Header = styled(FlexSpace)`
  align-items: center;
  flex-shrink: 0;
  margin: 0 -8px;
  padding: 0 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
`;

const Tabs = styled(FlexMiddle)`
  gap: 20px;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 4px 16px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0;
  text-align: center;
  color: ${({ $active, theme }) => ($active ? theme.colors.black : theme.colors.grey4)};
  border-bottom: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
`;

const HeaderActions = styled(FlexMiddle)`
  gap: 8px;
  flex-shrink: 0;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const ShiftRow = styled.div`
  box-sizing: border-box;
  height: 84px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${({ $selected, theme }) => ($selected ? theme.colors.primary : 'transparent')};
  background: ${({ $selected, theme }) => ($selected ? `${theme.colors.primary}1A` : theme.colors.white)};

  &:hover {
    background: ${({ $selected, theme }) => ($selected ? `${theme.colors.primary}1A` : theme.colors.grey1)};
  }
`;

const RowTop = styled(FlexSpace)`
  align-items: center;
`;

const ShiftDate = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0;
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.black};
`;

const ShiftTime = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  text-align: right;
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.grey4};
  white-space: nowrap;
`;

const ShiftTitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ShiftLocation = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  vertical-align: middle;
  color: ${({ theme }) => theme.colors.grey4};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EmptyState = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey4};
  padding: 16px 0;
  text-align: center;
`;

const CreateEmployeeTicketShiftPicker = ({ employeeId, branchId }) => {
  const { t, dateFormat, timeFormat } = useContext(ConfigContext);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const { input: shiftInput } = useField('shiftId');
  const { input: shiftDetailsInput } = useField('shiftDetails');

  const { applications, isLoading } = useEmployeeBookedShifts({ employeeId, branchId });

  const decoratedShifts = useMemo(() => {
    const todayMs = moment.utc().startOf('day').valueOf();
    return (applications || []).map((shift) => {
      const shiftDate = moment.utc(shift.date);
      const dateStr = shiftDate.format(`dddd, ${dateFormat || 'D MMM YYYY'}`);
      const timeStr = formatShiftTimeRange(shift, timeFormat);
      const titleStr = [shift.projectName, shift.positionName].filter(Boolean).join(', ');
      return {
        ...shift,
        dateStr,
        timeStr,
        titleStr,
        isUpcoming: shiftDate.valueOf() >= todayMs,
        searchText: [shift.projectName, shift.positionName, shift.locationName, dateStr, timeStr]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
      };
    });
  }, [applications, dateFormat, timeFormat]);

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    return query ? decoratedShifts.filter((shift) => shift.searchText.includes(query)) : decoratedShifts;
  }, [decoratedShifts, debouncedSearch]);

  const { upcoming, past } = useMemo(
    () =>
      filtered.reduce(
        (acc, shift) => {
          (shift.isUpcoming ? acc.upcoming : acc.past).push(shift);
          return acc;
        },
        { upcoming: [], past: [] }
      ),
    [filtered]
  );

  const didAutoSelectTab = useRef(false);
  useEffect(() => {
    if (!didAutoSelectTab.current && !isLoading && upcoming.length === 0 && past.length > 0) {
      didAutoSelectTab.current = true;
      setActiveTab('past');
    }
  }, [isLoading, upcoming.length, past.length]);

  const visibleShifts = activeTab === 'upcoming' ? upcoming : past;

  const renderRow = (shift) => {
    const isSelected = shiftInput.value === shift.shiftId;
    return (
      <ShiftRow
        key={shift.shiftId}
        $selected={isSelected}
        onClick={() => {
          shiftInput.onChange(isSelected ? null : shift.shiftId);
          shiftDetailsInput.onChange(isSelected ? null : toShiftDetails(shift));
        }}>
        <RowTop>
          <ShiftDate>{shift.dateStr}</ShiftDate>
          {shift.timeStr && <ShiftTime>{shift.timeStr}</ShiftTime>}
        </RowTop>
        {shift.titleStr && <ShiftTitle>{shift.titleStr}</ShiftTitle>}
        {shift.locationName && <ShiftLocation>{shift.locationName}</ShiftLocation>}
      </ShiftRow>
    );
  };

  return (
    <Container>
      <Header>
        <Tabs>
          {TABS.map((tab) => (
            <Tab key={tab} type="button" $active={activeTab === tab} onClick={() => setActiveTab(tab)}>
              {t(`help_desk_shifts_${tab}`)}
            </Tab>
          ))}
        </Tabs>
        <HeaderActions>
          <Search value={search} onChange={setSearch} searchIcon={SearchIcon} />
        </HeaderActions>
      </Header>
      <ScrollArea>
        {isLoading ? (
          <LoaderArea />
        ) : visibleShifts.length > 0 ? (
          visibleShifts.map(renderRow)
        ) : (
          <EmptyState>{t('help_desk_no_shifts_found')}</EmptyState>
        )}
      </ScrollArea>
    </Container>
  );
};

export default CreateEmployeeTicketShiftPicker;
